<template>
    <div class="card checkin-card position-relative">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="true"
                :showAddNew="false"
                title="Guest Check-In"
                @onClickCloseButton="goBack"
            />
        </div>

        <div class="px-2 mt-2" v-if="reservation">
            <div class="row">
                <div class="col-12 col-md-7 mb-2">
                    <section class="section-card mb-2">
                        <div class="section-card-header d-flex align-items-center gap-2">
                            <h4 class="mb-0">Guest Details</h4>
                            <span v-if="reservation.reservation_no" class="badge bg-light-primary">Reservation No: {{ reservation.reservation_no }}</span>
                        </div>
                        <div class="section-card-body">
                            <div class="guest-card">
                                <GuestSummary :guest="primaryGuest"/>
                            </div>
                            <div class="row g-1 checkin-dates mt-2">
                                <div class="col-12 col-sm-6">
                                    <label class="summary-label d-block mb-1">Date of Check-in</label>
                                    <input v-model="checkInDate" type="datetime-local" class="form-control" @change="onCheckInDateChange"/>
                                </div>
                                <div class="col-12 col-sm-6 d-flex align-items-end">
                                    <span><span class="summary-label">Date of Check-out</span> {{ checkOutDisplay }}</span>
                                </div>
                            </div>
                            <div class="booking-info mt-2">
                                <div class="row g-1">
                                    <div class="col-12 col-sm-6">
                                        <label class="summary-label d-block mb-1">Booked By</label>
                                        <AsyncSelect
                                            placeholder="Select staff"
                                            v-model="bookingInfo.booked_by"
                                            :api-service="fetchContactProfiles"
                                            :additional-query="{ type: 'employee' }"
                                            :format-label="formatPatientLabel"
                                        />
                                    </div>
                                    <div class="col-12 col-sm-6">
                                        <label class="summary-label d-block mb-1">Referrer</label>
                                        <AsyncSelect
                                            placeholder="Select referrer"
                                            v-model="bookingInfo.referrer"
                                            :api-service="fetchContactProfiles"
                                            :additional-query="{ type: 'agent' }"
                                            :format-label="formatPatientLabel"
                                        />
                                    </div>
                                    <div class="col-12">
                                        <label class="summary-label d-block mb-1">Notes</label>
                                        <textarea v-model="bookingInfo.notes" rows="2" class="form-control" placeholder="Notes"></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <RoomSummaryCard
                        v-model="selectedRooms"
                        :nights="nights"
                        :check-in="reservation.check_in"
                        :check-out="reservation.check_out"
                        :saving="savingRooms"
                        @onSelectRooms="openCheckinDetailsModal"
                        @onSave="saveCheckinDetails"
                    />

                    <AdditionalGuestsCard
                        :guests="additionalGuests"
                        :primary-guest-id="reservation.guest_id"
                        @onAddGuest="openGuestModal"
                        @onLinkGuest="linkGuest"
                        @onRemoveGuest="onRemoveGuest"
                    />
                </div>

                <div class="col-12 col-md-5 mb-2">
                    <HotelBillPanel
                        :ipd-register-id="ipdRegisterId"
                        :guest-id="reservation.guest_id"
                        :rooms="savedRooms"
                        :nights="nights"
                        :advance-receipts="advanceReceipts"
                        :has-unsaved-rooms="hasUnsavedRooms"
                        @onBillSaved="onBillSaved"
                    />
                </div>
            </div>
        </div>

        <Teleport to="body">
            <GuestModal
                v-if="showGuestModal"
                @onCreateProfile="onGuestCreated"
                @onClose="closeGuestModal"
            />

            <CheckinDetailsModal
                v-if="showCheckinDetailsModal"
                v-model="selectedRooms"
                :reservation="reservation"
                :own-room-ids="ownRoomIds"
                :nights="nights"
                @update:checkInDate="onModalCheckInDateChange"
                @update:checkOut="onModalCheckOutChange"
                @onClose="closeCheckinDetailsModal"
            />
        </Teleport>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import Loader from '@/components/atom/LoaderComponent';
import GuestModal from '@/components/molecule/company/hotel/hotel-checkin/GuestModal.vue';
import GuestSummary from '@/components/molecule/company/hotel/hotel-checkin/GuestSummary.vue';
import CheckinDetailsModal from '@/components/molecule/company/hotel/hotel-checkin/CheckinDetailsModal.vue';
import RoomSummaryCard from '@/components/molecule/company/hotel/hotel-checkin/RoomSummaryCard.vue';
import AdditionalGuestsCard from '@/components/molecule/company/hotel/hotel-checkin/AdditionalGuestsCard.vue';
import HotelBillPanel from '@/components/molecule/company/hotel/hotel-checkin/HotelBillPanel.vue';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import handleHotel from '@/services/modules/hotel/hotel';
import handleContact from '@/services/modules/contact';
import useDate from '@/services/utils/day';
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper';

const $route = useRoute();
const $router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const {
    fetchHotelCheckin,
    addHotelCheckinGuest,
    removeHotelCheckinGuest,
    fetchSingleHotelReservation,
    updateHotelReservation,
    checkHotelRoomAvailability,
} = handleHotel();
const { fetchContactProfiles } = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();
const { calendarDayDiff, formatForInput } = useDate();

const loading = ref(false);
const savingRooms = ref(false);
const reservation = ref(null);
const additionalGuests = ref([]);
const selectedRooms = ref([]);
const savedRooms = ref([]);
const ownRoomIds = ref([]);
const advanceReceipts = ref([]);
const showGuestModal = ref(false);
const showCheckinDetailsModal = ref(false);
const previousCheckInDate = ref(null);
const bookingInfo = ref({
    booked_by: null,
    referrer: null,
    notes: '',
});

const ipdRegisterId = computed(() => $route.params.ipdRegisterId);

const primaryGuest = computed(() => {
    if (! reservation.value) return {};
    return {
        guest_id: reservation.value.guest_id,
        guest_name: reservation.value.guest_name,
        guest_mobile: reservation.value.guest_mobile,
        guest_birthday: reservation.value.guest_birthday,
        guest_gender: reservation.value.guest_gender,
        guest_nid: reservation.value.guest_nid,
        guest_present_address: reservation.value.guest_present_address,
    };
});

const checkInDate = computed({
    get: () => {
        if (! reservation.value) return null;
        return reservation.value.check_in;
    },
    set: (value) => { reservation.value.check_in = value; },
});

const nights = computed(() => {
    if (! reservation.value) return 1;
    if (! reservation.value.check_in) return 1;
    if (! reservation.value.check_out) return 1;
    const diff = calendarDayDiff(reservation.value.check_in, reservation.value.check_out);
    if (diff < 1) return 1;
    return diff;
});

const shiftCheckOutDate = (previousCheckIn, newCheckIn) => {
    if (! previousCheckIn) return;
    if (! reservation.value.check_out) return;
    const dayDiff = differenceInCalendarDays(new Date(newCheckIn), new Date(previousCheckIn));
    if (! dayDiff) return;
    const shiftedDate = addDays(new Date(reservation.value.check_out), dayDiff);
    reservation.value.check_out = formatForInput(shiftedDate);
};

const splitCheckInParts = (checkInValue) => {
    const parts = String(checkInValue).split('T');
    reservation.value.check_in_date = parts[0];
    if (parts.length < 2) return;
    reservation.value.check_in_time = parts[1];
};

const syncCheckInDateParts = (dateValue) => {
    reservation.value.check_in_date = dateValue;
    if (! reservation.value.check_in_time) {
        reservation.value.check_in = dateValue;
        return;
    }
    reservation.value.check_in = `${dateValue}T${reservation.value.check_in_time}`;
};

const buildRoomAvailabilityQuery = () => {
    const roomIdParams = selectedRooms.value.map(room => `room_ids[]=${room.service_resource_id}`).join('&');
    return `${getBaseQuery()}&${roomIdParams}&check_in=${reservation.value.check_in}&check_out=${reservation.value.check_out}&ignore_reservation_id=${ipdRegisterId.value}`;
};

const warnIfRoomsUnavailable = async () => {
    if (! selectedRooms.value.length) return;
    await checkHotelRoomAvailability(buildRoomAvailabilityQuery()).then(res => {
        if (! res.status) return;
        if (! res.data.length) return;
        const conflictedRooms = selectedRooms.value.filter(room => res.data.includes(room.service_resource_id));
        const roomNames = conflictedRooms.map(room => room.name).join(', ');
        showError(`${roomNames} already booked for the selected check-in date. Please review room selection.`);
    });
};

const onCheckInDateChange = async (event) => {
    const previousCheckIn = previousCheckInDate.value;
    previousCheckInDate.value = event.target.value;
    splitCheckInParts(event.target.value);
    shiftCheckOutDate(previousCheckIn, event.target.value);
    await warnIfRoomsUnavailable();
};

const onModalCheckInDateChange = (value) => {
    const previousCheckIn = previousCheckInDate.value;
    syncCheckInDateParts(value);
    previousCheckInDate.value = reservation.value.check_in;
    shiftCheckOutDate(previousCheckIn, reservation.value.check_in);
};

const onModalCheckOutChange = (value) => {
    reservation.value.check_out = value;
};

const checkOutDisplay = computed(() => {
    if (! reservation.value) return '';
    if (! reservation.value.check_out) return '';
    return format(new Date(reservation.value.check_out), 'MMM d, yyyy');
});

const roomIdsOf = (rooms) => {
    return rooms.map(room => String(room.service_resource_id)).sort().join(',');
};

const hasUnsavedRooms = computed(() => {
    return roomIdsOf(selectedRooms.value) !== roomIdsOf(savedRooms.value);
});

const getBaseQuery = () => {
    return `?company_id=${$route.params.companyId}`;
};

const goBack = () => {
    const redirectRoute = $route.query.redirect_route;
    const routeQuery = {};
    if ($route.query.tab) routeQuery.tab = $route.query.tab;
    $router.push({
        name: redirectRoute || 'hotel-reservation',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId,
        },
        query: routeQuery,
    });
};

const loadCheckin = async () => {
    loading.value = true;
    await fetchHotelCheckin(ipdRegisterId.value, getBaseQuery()).then(res => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        reservation.value = res.data;
        additionalGuests.value = res.data.guests;
        previousCheckInDate.value = res.data.check_in;
    }).finally(() => {
        loading.value = false;
    });
};

const mapReservationRooms = (rooms) => {
    return rooms.map(room => {
        return {
            service_resource_id: room.service_resource_id,
            name: room.name,
            rate: room.rate,
        };
    });
};

const loadReservationRooms = async () => {
    await fetchSingleHotelReservation(getBaseQuery(), ipdRegisterId.value).then(res => {
        if (! res.status) return;
        selectedRooms.value = mapReservationRooms(res.data.rooms);
        savedRooms.value = mapReservationRooms(res.data.rooms);
        ownRoomIds.value = res.data.rooms.map(room => room.service_resource_id);
        advanceReceipts.value = resolveAdvanceReceipts(res.data.payments);
        bookingInfo.value = {
            booked_by: res.data.booked_by,
            referrer: res.data.referrer,
            notes: res.data.notes,
        };
    });
};

const resolveAdvanceReceipts = (payments) => {
    if (! payments) return [];
    return payments.filter(payment => {
        if (payment.receipt_type !== 'advance') return false;
        return Number(payment.available_amount) > 0;
    });
};

const openGuestModal = () => {
    showGuestModal.value = true;
};

const closeGuestModal = () => {
    showGuestModal.value = false;
};

const openCheckinDetailsModal = () => {
    showCheckinDetailsModal.value = true;
};

const closeCheckinDetailsModal = async () => {
    showCheckinDetailsModal.value = false;
    await warnIfRoomsUnavailable();
};

const linkGuest = async (contactProfileId) => {
    loading.value = true;
    const payload = {
        company_id: $route.params.companyId,
        contact_profile_id: contactProfileId,
    };
    await addHotelCheckinGuest(ipdRegisterId.value, payload).then(async (res) => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        await loadCheckin();
    }).finally(() => {
        loading.value = false;
    });
};

const onGuestCreated = async (profile) => {
    closeGuestModal();
    await linkGuest(profile.id);
};

const onRemoveGuest = async (guest) => {
    loading.value = true;
    await removeHotelCheckinGuest(ipdRegisterId.value, guest.guest_id, getBaseQuery()).then(async (res) => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        await loadCheckin();
    }).finally(() => {
        loading.value = false;
    });
};

const resolveContactId = (contact) => {
    if (! contact) return null;
    return contact.id;
};

const buildRoomsPayload = () => {
    return {
        company_id: $route.params.companyId,
        contact_profile_id: reservation.value.guest_id,
        registration_status: 'checked_in',
        check_in: reservation.value.check_in,
        check_out: reservation.value.check_out,
        rooms: selectedRooms.value,
        admitted_by: resolveContactId(bookingInfo.value.booked_by),
        referrer_id: resolveContactId(bookingInfo.value.referrer),
        notes: bookingInfo.value.notes,
    };
};

const saveCheckinDetails = async () => {
    savingRooms.value = true;
    await updateHotelReservation(buildRoomsPayload(), ipdRegisterId.value).then(async (res) => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        await loadReservationRooms();
        reservation.value.registration_status = 'checked_in';
    }).finally(() => {
        savingRooms.value = false;
    });
};

const goToReservationCheckedInTab = () => {
    $router.push({
        name: 'hotel-reservation',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId,
        },
        query: { reg_status: 'checked_in' },
    });
};

const onBillSaved = async () => {
    if (! reservation.value) return;
    await updateHotelReservation(buildRoomsPayload(), ipdRegisterId.value).then(async (res) => {
        if (! res.status) {
            showError(res.message);
            return;
        }
        goToReservationCheckedInTab();
    });
};

onMounted(async () => {
    await Promise.all([
        loadCheckin(),
        loadReservationRooms(),
    ]);
});
</script>

<style>
.section-card {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
}
.section-card-header {
    padding: 0.6rem 0.9rem;
    background: #f8f9fb;
    border-bottom: 1px solid #eef0f3;
}
.section-card-body {
    padding: 0.9rem;
}
</style>

<style scoped>
.checkin-card {
    min-height: 75vh;
    padding-bottom: 2rem;
}
.guest-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0.4rem 0.6rem;
    font-size: 0.82rem;
}
.checkin-dates {
    font-size: 0.85rem;
    color: #1f2937;
}
.checkin-dates .summary-label {
    font-weight: 600;
    color: #6b7280;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin-right: 0.3rem;
}
.booking-info {
    border-top: 1px solid #eef0f3;
    padding-top: 0.75rem;
}
.booking-info .summary-label {
    font-weight: 600;
    color: #6b7280;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}
</style>
